import React, { useEffect, useState, useCallback } from 'react';
import { Lightbulb, CheckCircle2, XCircle, Clock, Eye, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABEL = {
  suggested: { label: 'Nieuw voorstel', tone: 'emerald' },
  viewed:    { label: 'Bekeken',        tone: 'sky' },
  approved:  { label: 'Goedgekeurd',    tone: 'emerald' },
  simulated: { label: 'Gesimuleerd',    tone: 'emerald' },
  rejected:  { label: 'Afgewezen',      tone: 'amber' },
  expired:   { label: 'Verlopen',       tone: 'white/55' },
  failed:    { label: 'Mislukt',        tone: 'amber' },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  sky:     'bg-sky-500/15 text-sky-300 border-sky-400/30',
  amber:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
  'white/55': 'bg-white/10 text-white/55 border-white/20',
};

function StatusPill({ status, testId }) {
  const cfg = STATUS_LABEL[status] || { label: status, tone: 'white/55' };
  return (
    <span
      data-testid={testId}
      className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${TONE_CLASSES[cfg.tone]}`}
    >
      {cfg.label}
    </span>
  );
}

/**
 * DeviceSuggestionsPanel — klant-zichtbaar tabblad onderdeel.
 *
 * Toont pending/active device-action suggesties via `/api/devices/actions/*`.
 * Klant kan: bekijken, goedkeuren (= simulatie), afwijzen.
 *
 * BELANGRIJK:
 *  - Goedkeuren simuleert alleen. Er wordt nog niets automatisch geschakeld.
 *  - Geen PII in de UI of in netwerkverkeer.
 *  - Geen externe AI.
 */
export default function DeviceSuggestionsPanel({ sessionId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/devices/actions/list?limit=20`, {
        credentials: 'omit',
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'load_failed');
      setItems(j.items || []);
      setErr(null);
    } catch (e) {
      setErr(e.message || 'load_failed');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const logEvent = useCallback(async (eventName, meta) => {
    try {
      await fetch(`${API}/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: eventName,
          session_id: sessionId || 'mp-suggestions',
          meta: meta || {},
        }),
      });
    } catch { /* no-op */ }
  }, [sessionId]);

  const handleView = async (sid) => {
    setBusyId(sid);
    try {
      await fetch(`${API}/devices/actions/view/${sid}`, { method: 'POST' });
      logEvent('device_action_viewed', { suggestion_id: sid });
      await fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDecide = async (sid, decision) => {
    setBusyId(sid);
    try {
      await fetch(`${API}/devices/actions/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          suggestion_id: sid,
          decision,
          actor: 'customer',
        }),
      });
      logEvent(
        decision === 'approved' ? 'device_action_approved' : 'device_action_rejected',
        { suggestion_id: sid }
      );
      await fetchList();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-3" data-testid="mp-device-suggestions">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
          <Lightbulb className="w-4 h-4" />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-semibold">Suggesties van Powersure</div>
          <div className="text-sm text-white/65 mt-0.5">
            Slimme voorstellen op basis van uw apparaten en tarieven.
          </div>
        </div>
      </div>

      <div
        className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] p-3 flex items-start gap-2"
        data-testid="mp-device-suggestions-safety-note"
      >
        <ShieldCheck className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
        <p className="text-[12.5px] text-white/75 leading-relaxed">
          <span className="text-emerald-200 font-medium">Er wordt nog niets automatisch geschakeld.</span>
          {' '}Goedkeuren start alleen een veilige simulatie. U houdt zelf de controle.
        </p>
      </div>

      {loading && (
        <div className="text-sm text-white/55" data-testid="mp-device-suggestions-loading">
          Suggesties laden…
        </div>
      )}

      {!loading && err && (
        <div
          className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-200"
          data-testid="mp-device-suggestions-error"
        >
          Kon suggesties niet ophalen. Probeer het later opnieuw.
        </div>
      )}

      {!loading && !err && items.length === 0 && (
        <div
          className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/65"
          data-testid="mp-device-suggestions-empty"
        >
          Nog geen suggesties. Voeg eerst apparaten toe of wacht op uw eerste advies.
        </div>
      )}

      {!loading && !err && items.length > 0 && (
        <ul className="space-y-2" data-testid="mp-device-suggestions-list">
          {items.map((s) => {
            const isTerminal = ['simulated', 'rejected', 'expired', 'failed', 'approved'].includes(s.status);
            return (
              <li
                key={s.suggestion_id}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5"
                data-testid={`mp-suggestion-${s.suggestion_id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                  <div className="text-[13.5px] font-semibold text-white leading-tight">
                    {s.action?.replace(/_/g, ' ') || 'Voorstel'}
                  </div>
                  <StatusPill status={s.status} testId={`mp-suggestion-status-${s.suggestion_id}`} />
                </div>
                <div className="text-[12px] text-white/65 leading-snug mb-2">
                  <span className="text-white/75">Apparaat:</span> {s.device_kind?.replace(/_/g, ' ') || '—'}
                  {' · '}
                  <span className="text-white/75">Waarom:</span> {s.rationale || 'Energie-besparing'}
                </div>
                {typeof s.expected_savings_eur_cents === 'number' && s.expected_savings_eur_cents > 0 && (
                  <div className="text-[11.5px] text-emerald-300 mb-2">
                    Geschatte besparing: ~€{(s.expected_savings_eur_cents / 100).toFixed(2)} <span className="text-white/45">(indicatie, geen garantie)</span>
                  </div>
                )}
                {s.capability_snapshot?.requires_approval && (
                  <div className="text-[11px] text-white/55 mb-2">
                    Risico/comfortimpact:{' '}
                    {s.capability_snapshot?.read_only ? 'geen — alleen inzicht.' : 'klein — wordt gesimuleerd.'}
                  </div>
                )}

                {!isTerminal && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {s.status === 'suggested' && (
                      <button
                        type="button"
                        onClick={() => handleView(s.suggestion_id)}
                        disabled={busyId === s.suggestion_id}
                        data-testid={`mp-suggestion-view-${s.suggestion_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 text-[12px] border border-sky-400/30 transition-colors disabled:opacity-50"
                      >
                        <Eye className="w-3 h-3" /> Bekijken
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDecide(s.suggestion_id, 'approved')}
                      disabled={busyId === s.suggestion_id}
                      data-testid={`mp-suggestion-approve-${s.suggestion_id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[12px] border border-emerald-400/30 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-3 h-3" /> Goedkeuren (simulatie)
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecide(s.suggestion_id, 'rejected')}
                      disabled={busyId === s.suggestion_id}
                      data-testid={`mp-suggestion-reject-${s.suggestion_id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-white/65 text-[12px] border border-white/15 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3 h-3" /> Afwijzen
                    </button>
                  </div>
                )}
                {s.status === 'simulated' && (
                  <div className="text-[11px] text-emerald-300/90 mt-1 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Veilig gesimuleerd — geen sturing uitgevoerd.
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
